/** Lazy factory bridging vfx.json configs to VFX primitive instances (ADR-069). */
import type { VfxPrimitiveConfig } from './primitives/VfxPrimitive';
import { VfxPrimitive } from './primitives/VfxPrimitive';
import { ParticleBurst, ParticleBurstConfig } from './primitives/ParticleBurst';
import { TrailRenderer, TrailRendererConfig } from './primitives/TrailRenderer';
import { AuraRing, AuraRingConfig } from './primitives/AuraRing';
import { ZoneOverlay, ZoneOverlayConfig } from './primitives/ZoneOverlay';
import { StatusIndicator, StatusIndicatorConfig } from './primitives/StatusIndicator';
import { ImpactFlash, ImpactFlashConfig } from './primitives/ImpactFlash';
import { ProjectileTrail, ProjectileTrailConfig } from './primitives/ProjectileTrail';
import { ScreenDistortion, ScreenDistortionConfig } from './primitives/ScreenDistortion';
import { OrbitalSprites, OrbitalSpritesConfig } from './primitives/OrbitalSprites';
import { ChannelBeam, ChannelBeamConfig } from './primitives/ChannelBeam';
import { SlashArc, SlashArcConfig } from './primitives/SlashArc';
import { GroundFissure, GroundFissureConfig } from './primitives/GroundFissure';
import { ChainTether, ChainTetherConfig } from './primitives/ChainTether';
import { SpiralVortex, SpiralVortexConfig } from './primitives/SpiralVortex';
import { VfxConfigLoader } from './VfxConfigLoader';

export class VfxFactory {
  private scene: Phaser.Scene;
  private instances: Map<string, VfxPrimitive<VfxPrimitiveConfig>> = new Map();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Get or create the VFX primitive for a given effect key. Lazy — created on first request. */
  get(key: string): VfxPrimitive<VfxPrimitiveConfig> {
    const existing = this.instances.get(key);
    if (existing) return existing;
    const config = VfxConfigLoader.get(key);
    const type = VfxConfigLoader.getPrimitiveType(key);
    const instance = this.createPrimitive(type, config);
    this.instances.set(key, instance);
    return instance;
  }

  /** Destroy all instances — called on scene shutdown. */
  destroyAll(): void {
    for (const instance of this.instances.values()) {
      instance.destroy();
    }
    this.instances.clear();
  }

  private createPrimitive(
    type: string,
    config: VfxPrimitiveConfig,
  ): VfxPrimitive<VfxPrimitiveConfig> {
    switch (type) {
      case 'ParticleBurst':
        return new ParticleBurst(this.scene, config as ParticleBurstConfig);
      case 'TrailRenderer':
        return new TrailRenderer(this.scene, config as TrailRendererConfig);
      case 'AuraRing':
        return new AuraRing(this.scene, config as AuraRingConfig);
      case 'ZoneOverlay':
        return new ZoneOverlay(this.scene, config as ZoneOverlayConfig);
      case 'StatusIndicator':
        return new StatusIndicator(this.scene, config as StatusIndicatorConfig);
      case 'ImpactFlash':
        return new ImpactFlash(this.scene, config as ImpactFlashConfig);
      case 'ProjectileTrail':
        return new ProjectileTrail(this.scene, config as ProjectileTrailConfig);
      case 'ScreenDistortion':
        return new ScreenDistortion(this.scene, config as ScreenDistortionConfig);
      case 'OrbitalSprites':
        return new OrbitalSprites(this.scene, config as OrbitalSpritesConfig);
      case 'ChannelBeam':
        return new ChannelBeam(this.scene, config as ChannelBeamConfig);
      case 'SlashArc':
        return new SlashArc(this.scene, config as SlashArcConfig);
      case 'GroundFissure':
        return new GroundFissure(this.scene, config as GroundFissureConfig);
      case 'ChainTether':
        return new ChainTether(this.scene, config as ChainTetherConfig);
      case 'SpiralVortex':
        return new SpiralVortex(this.scene, config as SpiralVortexConfig);
      default:
        throw new Error(`Unknown VFX primitive type: ${type}`);
    }
  }
}
